import React, { useEffect, useMemo, useState } from "react";
import { 
    FileText,
    MoreHorizontal,
    ArrowUp as ArrowUpIcon,
    Calendar,
    Users,
    Layers,
 } from "lucide-react";
import { 
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    ComposedChart,
    ReferenceLine
} from 'recharts';
import TopNavigation from "./TopNavigation";

const generateHistoricalData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct'];
    return months.map(month => ({
        name: month + " '25",
        docProcessingTime: parseFloat((Math.random() * 10 + 2).toFixed(2)), 
        docFileCount: Math.floor(Math.random() * 500000 + 100000),
        ocrPercentage: parseFloat((90 + Math.random() * 10).toFixed(1)),
        fsccProcessingTime: parseFloat((Math.random() * 15 + 5).toFixed(2)),
        fsccFileCount: Math.floor(Math.random() * 600000 + 200000),
        uptime: parseFloat((99 + Math.random()).toFixed(2))
    }));
};

const PerformanceReport = ({ historicalData }) => {
    // Local state for filters
    const [selectedYear, setSelectedYear] = useState('2025');
    const [selectedCustomer, setSelectedCustomer] = useState('easyJet');

    // Filter Logic (Simulated)
    const baseData = useMemo(() => {
        return historicalData?.length ? historicalData : generateHistoricalData();
    }, [historicalData]);

    // For now filters do nothing
    const filteredData = useMemo(() => baseData, [baseData, selectedYear, selectedCustomer]);


    return (
        <div className="mx-auto">
            <div className="space-y-6 animate-in slide-in-from-right duration-500 p-6">
                <TopNavigation />
                {/* Report Header & Filters */}
                <div className="bg-slate-900 p-6 rounded-xl shadow-lg text-white flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-3">
                            <FileText className="text-emerald-400" />
                            Document Processing Performance
                        </h1>
                        <p className="text-slate-400 text-sm mt-1">Monthly performance aggregation and OCR accuracy reports</p>
                    </div>
                    
                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-4 bg-slate-800/50 p-2 rounded-lg border border-slate-700">
                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider ml-1">Year</label>
                            <div className="relative">
                                <Calendar className="absolute left-2.5 top-2 text-slate-400" size={14} />
                                <select 
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className="pl-8 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors"
                                >
                                    <option>2025</option>
                                    <option>2024</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-[10px] uppercase font-bold text-slate-400 tracking-wider ml-1">Customer</label>
                            <div className="relative">
                                <Users className="absolute left-2.5 top-2 text-slate-400" size={14} />
                                <select 
                                    value={selectedCustomer}
                                    onChange={(e) => setSelectedCustomer(e.target.value)}
                                    className="pl-8 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors min-w-[140px]"
                                >
                                    <option value="easyJet">easyJet</option>
                                    <option value="Ryanair">Ryanair</option>
                                    <option value="Lufthansa">Lufthansa</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Row Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Chart 1: Doc Feed Processing Times */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1">
                        <h3 className="font-semibold text-slate-800 mb-4 flex items-center justify-between">
                            Document Feed Processing Times
                            <MoreHorizontal size={16} className="text-slate-400 cursor-pointer" />
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{fontSize: 10}} />
                                    <YAxis yAxisId="left" label={{ value: 'Time (Min)', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#94a3b8' }} tick={{fontSize: 10}} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${(v/1000)}k`} tick={{fontSize: 10}} />
                                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <Legend wrapperStyle={{fontSize: '11px', paddingTop: '10px'}} />
                                    <Bar yAxisId="left" dataKey="docProcessingTime" name="Processing Time" fill="#10b981" radius={[4, 4, 0, 0]} barSize={30} />
                                    <Line yAxisId="right" type="monotone" dataKey="docFileCount" name="File Count" stroke="#1e293b" strokeWidth={2} dot={{r: 3, fill: '#1e293b'}} strokeDasharray="3 3" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart 2: Doc Feed OCR% */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1">
                        <h3 className="font-semibold text-slate-800 mb-4 flex items-center justify-between">
                            Document Feed OCR%
                            <MoreHorizontal size={16} className="text-slate-400 cursor-pointer" />
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{fontSize: 10}} />
                                    <YAxis domain={[80, 105]} tick={{fontSize: 10}} />
                                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <ReferenceLine y={100} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Target', position: 'insideTopRight', fill: '#10b981', fontSize: 10 }} />
                                    <Line type="stepAfter" dataKey="ocrPercentage" stroke="#10b981" strokeWidth={2} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Table: Summary */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1 flex flex-col">
                        <h3 className="font-semibold text-slate-800 mb-4">Summary Metrics</h3>
                        <div className="overflow-x-auto flex-1">
                            <table className="w-full text-xs text-left">
                                <thead className="text-slate-500 uppercase bg-slate-50">
                                    <tr>
                                        <th className="px-2 py-2">Metric</th>
                                        <th className="px-2 py-2 text-right">Value</th>
                                        <th className="px-2 py-2 text-right">Target</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    <tr>
                                        <td className="px-2 py-3 font-medium text-slate-700">Feed File Count</td>
                                        <td className="px-2 py-3 text-right">1,325,959</td>
                                        <td className="px-2 py-3 text-right text-slate-400">-</td>
                                    </tr>
                                    <tr>
                                        <td className="px-2 py-3 font-medium text-slate-700">Avg Proc Time</td>
                                        <td className="px-2 py-3 text-right text-emerald-600 font-bold">6.86 min</td>
                                        <td className="px-2 py-3 text-right text-slate-400">7.00</td>
                                    </tr>
                                    <tr>
                                        <td className="px-2 py-3 font-medium text-slate-700">Feed OCR%</td>
                                        <td className="px-2 py-3 text-right text-emerald-600 font-bold">99.7%</td>
                                        <td className="px-2 py-3 text-right text-slate-400">99.0%</td>
                                    </tr>
                                    <tr>
                                        <td className="px-2 py-3 font-medium text-slate-700">FSCC File Count</td>
                                        <td className="px-2 py-3 text-right">2,256,892</td>
                                        <td className="px-2 py-3 text-right text-slate-400">-</td>
                                    </tr>
                                    <tr>
                                        <td className="px-2 py-3 font-medium text-slate-700">FSCC Proc Time</td>
                                        <td className="px-2 py-3 text-right text-blue-600 font-bold">3.82 sec</td>
                                        <td className="px-2 py-3 text-right text-slate-400">5.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Bottom Row Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Chart 3: FSCC Processing Times */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1">
                        <h3 className="font-semibold text-slate-800 mb-4">FSCC Processing Times by Month</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{fontSize: 10}} />
                                    <YAxis yAxisId="left" label={{ value: 'Duration (Sec)', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#94a3b8' }} tick={{fontSize: 10}} />
                                    <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${(v/1000)}k`} tick={{fontSize: 10}} />
                                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <Bar yAxisId="left" dataKey="fsccProcessingTime" name="Duration (Sec)" fill="#0ea5e9" radius={[4, 4, 0, 0]} barSize={30} />
                                    <Line yAxisId="right" type="monotone" dataKey="fsccFileCount" name="File Count" stroke="#1e293b" strokeWidth={2} dot={{r: 3, fill: '#1e293b'}} strokeDasharray="3 3" />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart 4: FSCC OCR% */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1">
                        <h3 className="font-semibold text-slate-800 mb-4">FSCC OCR%</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={filteredData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="name" tick={{fontSize: 10}} />
                                    <YAxis domain={[90, 110]} tick={{fontSize: 10}} />
                                    <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <ReferenceLine y={100} stroke="#10b981" strokeDasharray="3 3" />
                                    <Line type="monotone" dataKey="ocrPercentage" stroke="#1e293b" strokeWidth={2} dot={{r: 4}} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Chart 5: Uptime Percentage */}
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 lg:col-span-1">
                        <h3 className="font-semibold text-slate-800 mb-4">Uptime % (Combined Services)</h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={filteredData} layout="vertical" margin={{left: 20}}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                                    <XAxis type="number" domain={[99, 100]} hide />
                                    <YAxis dataKey="name" type="category" tick={{fontSize: 10}} width={40} />
                                    <Tooltip cursor={{fill: 'transparent'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <Bar dataKey="uptime" fill="#10b981" radius={[0, 4, 4, 0]} barSize={20}>
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PerformanceReport;